import React, { useEffect, useState } from "react";
import {
  KeyRound, Loader2, RefreshCw, Save, Eye, EyeOff, Plug, CheckCircle2,
  XCircle, AlertTriangle,
} from "lucide-react";
import { adminIntegrations, adminSaveIntegrations, adminTestIntegration } from "@/lib/api";

/* Admin -> Integrations: view & edit every third-party API key/config at runtime.
   Secrets are never sent to the browser in full — only a "••••last4" hint. Leaving
   a secret's field on its mask keeps the stored key; typing a new value replaces it;
   clearing the field reverts to the value the server was deployed with (.env). */

// Which services have a live "Test" probe on the backend.
const TESTABLE = { OpenAI: "openai", Apify: "apify", "Brevo (email)": "brevo", "Razorpay (payments)": "razorpay" };

export default function AdminIntegrations() {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});        // key -> new value (dirty fields only)
  const [reveal, setReveal] = useState({});       // key -> bool (show secret input as text)
  const [tests, setTests] = useState({});         // group -> {ok, detail, busy}
  const [flash, setFlash] = useState(null);

  const load = () => {
    setBusy(true);
    adminIntegrations()
      .then(({ data }) => { setGroups(data.groups || []); setEdits({}); })
      .catch(() => setGroups([]))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const dirty = Object.keys(edits).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await adminSaveIntegrations(edits);
      setGroups(data.groups || []);
      setEdits({});
      const n = (data.changed || []).length;
      setFlash({ ok: true, msg: n ? `Saved — ${n} value${n > 1 ? "s" : ""} updated. Live now.` : "Nothing to change." });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Save failed." });
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(null), 4000);
    }
  };

  const runTest = async (group) => {
    const svc = TESTABLE[group];
    if (!svc) return;
    setTests((t) => ({ ...t, [group]: { busy: true } }));
    try {
      const { data } = await adminTestIntegration(svc);
      setTests((t) => ({ ...t, [group]: { ...data, busy: false } }));
    } catch (e) {
      setTests((t) => ({ ...t, [group]: { ok: false, detail: e?.response?.data?.detail || "Test failed.", busy: false } }));
    }
  };

  const setField = (key, val) => setEdits((e) => ({ ...e, [key]: val }));

  if (groups === null) {
    return <div className="text-stone-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading integrations…</div>;
  }

  return (
    <div data-testid="admin-integrations">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100 flex items-center gap-2"><KeyRound size={26} className="text-[#C2410C]" /> Integrations</h1>
          <p className="text-sm text-stone-400 mt-1 max-w-2xl">
            Every third-party key the platform uses. Edits apply <b className="text-stone-200">instantly</b> across the running server — no redeploy.
            Fields marked <span className="text-amber-300">restart</span> take effect on the next restart.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-700 text-stone-200 text-sm hover:bg-stone-800 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
          <button onClick={save} disabled={!dirty || saving} data-testid="integrations-save"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes{dirty ? ` (${Object.keys(edits).length})` : ""}
          </button>
        </div>
      </div>

      {flash && (
        <div className={`mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${flash.ok ? "bg-emerald-900/30 border border-emerald-800 text-emerald-200" : "bg-red-900/30 border border-red-800 text-red-200"}`}>
          {flash.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {flash.msg}
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-900/15 border border-amber-800/50 text-amber-200/90 text-xs">
        <AlertTriangle size={15} className="shrink-0 mt-0.5" />
        <span>Secrets are masked (<code className="font-mono">••••last4</code>) and never leave the server in full. Leave a secret field untouched to keep the current key; type to replace it; clear it to fall back to the deployed <code className="font-mono">.env</code> value.</span>
      </div>

      <div className="mt-6 grid gap-5">
        {groups.map((g) => {
          const t = tests[g.group];
          const testable = TESTABLE[g.group];
          return (
            <div key={g.group} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-6" data-testid={`integration-group-${g.group}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display text-xl text-stone-100">{g.group}</h2>
                  {g.note && <p className="text-xs text-stone-500 mt-0.5 max-w-xl">{g.note}</p>}
                </div>
                {testable && (
                  <button onClick={() => runTest(g.group)} disabled={t?.busy}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-stone-700 text-stone-200 text-xs hover:bg-stone-800 disabled:opacity-60">
                    {t?.busy ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />} Test connection
                  </button>
                )}
              </div>

              {t && !t.busy && (
                <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${t.ok ? "bg-emerald-900/30 text-emerald-300" : "bg-red-900/30 text-red-300"}`}>
                  {t.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {t.detail}
                </div>
              )}

              <div className="mt-4 grid gap-3">
                {g.items.map((it) => {
                  const editing = it.key in edits;
                  const showReveal = it.secret && !editing;
                  const value = editing ? edits[it.key] : it.value;
                  return (
                    <div key={it.key} className="grid md:grid-cols-[220px,1fr] gap-2 md:gap-4 md:items-center">
                      <div className="min-w-0">
                        <div className="text-sm text-stone-200 flex items-center gap-2 flex-wrap">
                          {it.label}
                          {it.secret && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-stone-800 text-stone-400">secret</span>}
                          {it.restart && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300">restart</span>}
                        </div>
                        <div className="text-[11px] text-stone-500 font-mono mt-0.5 flex items-center gap-1.5">
                          {it.key}
                          <span className={`px-1 rounded ${it.source === "custom" ? "text-[#F59E0B]" : it.is_set ? "text-emerald-500" : "text-stone-600"}`}>
                            · {it.source === "custom" ? "custom" : it.source === "env" ? "from .env" : "not set"}
                          </span>
                        </div>
                      </div>
                      <div className="relative flex items-center gap-2">
                        <input
                          type={it.secret && !editing && !reveal[it.key] ? "text" : (it.secret && editing && !reveal[it.key] ? "password" : "text")}
                          value={value}
                          onChange={(e) => setField(it.key, e.target.value)}
                          placeholder={it.secret ? "•••• leave to keep, type to replace, clear to reset" : "not set"}
                          data-testid={`integration-input-${it.key}`}
                          autoComplete="off"
                          spellCheck={false}
                          className={`w-full rounded-lg bg-stone-900/60 border px-3 py-2.5 text-sm text-stone-100 font-mono placeholder:text-stone-600 focus:outline-none focus:border-[#C2410C] ${editing ? "border-[#C2410C]/60" : "border-stone-700"}`}
                        />
                        {it.secret && editing && (
                          <button type="button" onClick={() => setReveal((r) => ({ ...r, [it.key]: !r[it.key] }))}
                            className="shrink-0 p-2 rounded-lg text-stone-400 hover:text-stone-100" title={reveal[it.key] ? "Hide" : "Show"}>
                            {reveal[it.key] ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        )}
                        {editing && (
                          <button type="button" onClick={() => setEdits(({ [it.key]: _, ...rest }) => rest)}
                            className="shrink-0 text-[11px] text-stone-500 hover:text-stone-300 underline whitespace-nowrap">undo</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
